import { useMemo } from "react";
import { Layer, Marker, Source } from "react-map-gl";
import type { Feature, FeatureCollection, LineString } from "geojson";

import { formatDistance, haversineMeters } from "@/lib/geo";
import type { MountpointPosition, RoverPosition } from "@/types/api";

interface Props {
  rovers: Map<string, RoverPosition>;
  mountpoints: Map<string, MountpointPosition>;
}

type Severity = "ok" | "warn" | "bad";

interface Pair {
  key: string;
  midLon: number;
  midLat: number;
  distanceM: number;
  label: string;
  severity: Severity;
}

const SOURCE_ID = "rover-mp-lines";
const LAYER_ID = "rover-mp-lines-layer";

const LINE_COLORS: Record<Severity, string> = {
  ok: "#10b981",   // < 3 km
  warn: "#f59e0b", // 3–4 km
  bad: "#ef4444",  // > 4 km
};

function severityFor(distanceM: number): Severity {
  if (distanceM < 3000) return "ok";
  if (distanceM < 4000) return "warn";
  return "bad";
}

export function MapDistanceLines({ rovers, mountpoints }: Props) {
  const { features, pairs } = useMemo(() => {
    const features: Feature<LineString>[] = [];
    const pairs: Pair[] = [];
    rovers.forEach((rover) => {
      // A stale rover's baseline would be a frozen number — don't draw it.
      if (rover.stale) return;
      const mp = mountpoints.get(rover.mountpoint);
      if (!mp) return;
      const distanceM = haversineMeters(rover.lat, rover.lon, mp.lat, mp.lon);
      const label = formatDistance(distanceM);
      const severity = severityFor(distanceM);
      const key = `${rover.session_id}__${mp.name}`;
      features.push({
        type: "Feature",
        properties: {
          distance_m: distanceM,
          label,
          rover: rover.session_id,
          mp: mp.name,
          color: LINE_COLORS[severity],
        },
        geometry: {
          type: "LineString",
          coordinates: [
            [rover.lon, rover.lat],
            [mp.lon, mp.lat],
          ],
        },
      });
      pairs.push({
        key,
        midLon: (rover.lon + mp.lon) / 2,
        midLat: (rover.lat + mp.lat) / 2,
        distanceM,
        label,
        severity,
      });
    });
    const fc: FeatureCollection<LineString> = { type: "FeatureCollection", features };
    return { features: fc, pairs };
  }, [rovers, mountpoints]);

  if (pairs.length === 0) return null;

  return (
    <>
      <Source id={SOURCE_ID} type="geojson" data={features}>
        <Layer
          id={LAYER_ID}
          type="line"
          layout={{ "line-cap": "round", "line-join": "round" }}
          paint={{
            "line-color": ["get", "color"],
            "line-width": 2,
            "line-opacity": 0.75,
            "line-dasharray": [2, 2],
          }}
        />
      </Source>
      {pairs.map((p) => (
        <Marker key={p.key} longitude={p.midLon} latitude={p.midLat} anchor="center">
          <div
            className={`map-distance-pill map-distance-pill--${p.severity}`}
            title={`Baseline: ${p.label}`}
          >
            {p.label}
          </div>
        </Marker>
      ))}
    </>
  );
}
